import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import './MatchSchedule.css';
import CrewDropZone from './CrewDropZone';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import RemoveIcon from '@mui/icons-material/Remove';
import { CalendarIcon } from '@mui/x-date-pickers';
import { ArrowDownwardRounded, ExpandLess, ExpandMore, Label, Print } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import CentralCalls from '../../centralCalls';
import { DndProvider, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import dayjs from 'dayjs';
import CrewSelector from './CrewSelector';
import ActivePhaseButtonGroup from '../phase/activePhaseButtonGroup';
import FilterButtonGroup from '../phase/filterButtonGroup';
import ScheduleColumn from './ScheduleColumn';
import TeamDropDown from './TeamDropDown';
import DraggableCrewList from './DraggableCrewList';
import PhaseFilter from './PhaseFilter';
import CrewFilter from './CrewFilter';
import ReadyStatFilter from './ReadyStatFilter';
import BuilderFilter from './BuilderFilter';
import ScheduleColumnSummary from './ScheduleColumnSummary';
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { json } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const crewColors = [
    '#ffebee', '#f8bbd0', '#e1bee7', '#d1c4e9', '#bbdefb',
    '#b3e5fc', '#b2dfdb', '#dcedc8', '#fff9c4', '#ffe0b2'
];

function MatchSchedule() {
    //const [employees, setEmployees] = useState([]);
    const [crews, setCrews] = useState([]);
    const [allCrews, setAllCrews] = useState([]);
    const [addedCrews, setAddedCrews] = useState([]);
    const [selectedCrewOid, setSelectedCrewOid] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [filterPhaseType, setFilterPhaseType] = useState([]);
    const [filterBuilder, setFilterBuilder] = useState([]);
    const [filterCrew, setFilterCrew] = useState([]);
    const [filterPhaseStatus, setFilterPhaseStatus] = useState([]);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [refreshKeyByDate, setRefreshKeyByDate] = useState({});
    const [crewDialogOpen, setCrewDialogOpen] = useState(false);
    // Initialize with three dates: today, tomorrow, and day after tomorrow
    //const initialDates = [dayjs().add(-1, 'day'), dayjs().add(1, 'day'), dayjs().add(2, 'day')];
    const initialDates = [dayjs('1900-01-01'), dayjs().add(0, 'day')];
    const [startDate, setStartDate] = useState(dayjs().add(0, 'day'));
    const [columns, setColumns] = useState(initialDates);
    const [collapsed, setCollapsed] = useState(() => columns.map(() => false));
    const [refreshCrewList, setRefreshCrewList] = useState(false);
    const [teamDropDownKey, setTeamDropDownKey] = useState(800);
    //const [customDates, setCustomDates] = useState([]);
    // Centralized jobs state: { '2023-10-05': [...jobs], '2023-10-06': [...jobs], ... }
    const [jobsByDate, setJobsByDate] = useState([]);
    const columnRefs = useRef([]);

    const fetchCrews = useCallback(async () => {
        try {
            const result = await CentralCalls.callStoredProcedureWithParams('sp_getCrewList', {  });
           
            setAllCrews(result);
             const primaryCrews = result.filter(crew => crew.is_primary === 1);
            setCrews(primaryCrews);
            // Remove any addedCrews that are now primary
            setAddedCrews(prevAddedCrews =>
                prevAddedCrews.filter(crew =>
                    !result.some(primaryCrew => primaryCrew.team_oid === crew.team_oid)
                )
            );
            setTeamDropDownKey(prev => prev + 1);
            return result;
        } catch (error) {
            console.error('Failed to fetch crews:', error);
            throw error;
        }
    }, []);
    const navigate = useNavigate();

const handlePrintTicketsClick = (date) => {
    const path = '/ticketPrintOut?team_oid=' + encodeURIComponent(selectedCrewOid) + '&start_date=' + encodeURIComponent(date.format('YYYY-MM-DD'));
    window.open(path, '_blank');
};
    // Initial fetch and refresh when refreshCrewList changes
    useEffect(() => {
        fetchCrews().catch(console.error);
    }, [fetchCrews, refreshCrewList]);

    // Make fetchCrews available for child components
    const refreshCrews = useCallback(async () => {
        await fetchCrews();
        setRefreshCrewList(prev => !prev);
        setTeamDropDownKey(prev => prev + 1);

    }, [fetchCrews]);
    const addCustomDate = () => {
        setColumns(prevDates => {
            const newDate = startDate.add(prevDates.length - 1, 'day'); // Start at today + 2 days
            return [...prevDates, newDate];
        });
    };
    const removeCustomDate = () => {
        if (columns.length > 2) {
            setColumns(prevDates => prevDates.slice(0, -1)); // Remove last date
        }

    };
    const handleBuilderSelect = (builder) => {
        setFilterBuilder(builder);
    };
    const handleCrewFilterSelect = (crew) => {
        setFilterCrew(crew);
    };
    const handlePhaseTypeSelect = (phase) => {
        setFilterPhaseType(phase);
    };
    const handlePhaseStatusSelect = (status) => {
        setFilterPhaseStatus(status);
    };
    const [isLoading, setIsLoading] = useState(false);

    const onRefresh = () => { setRefreshKey(refreshKey + 1) }
    const createUniqueCrews = (jobs, employees) => {
        const crewMap = new Map(); // Map to store unique crews
        for (const job of jobs) {
            if (!job?.employee_oids || job?.employee_oids.length === 0) continue;

            // Ensure employee_oids is sorted to avoid duplicate crews in different orders
            const sortedEmployeeOids = [...job.employee_oids].sort((a, b) => a - b);
            const crewKey = JSON.stringify(sortedEmployeeOids); // Unique identifier for crew

            if (!crewMap.has(crewKey)) {
                // Get employee details from the employees array
                const crewMembers = sortedEmployeeOids.map(oid => employees.find(emp => emp.oid === oid)).filter(Boolean);

                if (crewMembers.length > 0) {
                    //const crewName = crewMembers[0].name; // Crew name is the last name of first employee
                    const nameParts = crewMembers[0].name.trim().split(/\s+/);
                    const crewName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0]; // Last name or first if no last name

                    crewMap.set(crewKey, {
                        crewName,
                        employees: crewMembers
                    });
                }
            }
        }
        return Array.from(crewMap.values());
    };

    const fetchJobs = useCallback(async (columnIndex, newDate) => {

        const formattedNewDate = newDate.format('YYYY-MM-DD');
        let jobs = [];


        setColumns(prevColumns => {
            const updatedColumns = [...prevColumns];
            updatedColumns[columnIndex] = newDate;
            return updatedColumns;
        });
    }, [filterPhaseType, filterPhaseStatus, filterBuilder?.length]); // Dependencies

    const addNewCrew = () => {
        setCrewDialogOpen(true);
        // setCrews(prevCrews => {
        //     const newCrews = [...prevCrews, []];
        //     setSelectedCrewIndex(newCrews.length - 1);
        //     return newCrews;
        // });
    };
    const addExistingCrew = (crew) => {
        setAddedCrews(prevCrews => [...prevCrews, crew]);
    };
    const deleteCrew = (index) => {
        if (addedCrews.length > 0) {
            const updatedCrews = addedCrews.filter((_, i) => i !== index);
            setAddedCrews(updatedCrews);
            //setSelectedCrewIndex(0);
        }
    };
    const refreshColumnByDate = (date) => {
        const key = dayjs(date).format('YYYY-MM-DD');
        setRefreshKeyByDate(prev => ({
            ...prev,
            [key]: Date.now(),
        }));
    };

    const handleFixMissingPhaseStatusAll = async () => {
        try {
            await CentralCalls.callStoredProcedureWithParams('sp_forceInsertMissingPhaseStatusAll', {});
            refreshColumnByDate('1900-01-01');
        } catch (error) {
            console.error('Failed to run sp_forceInsertMissingPhaseStatusAll:', error);
        }
    };

    /**
     * Move a job from one position to another, possibly across different dates.
     * @param {string} sourceDate - The date from which the job is moved (formatted 'YYYY-MM-DD').
     * @param {number} sourceIndex - The index of the job in the source date.
     * @param {string} targetDate - The date to which the job is moved (formatted 'YYYY-MM-DD').
     * @param {number} targetIndex - The index in the target date where the job should be inserted.
     */

    const setJobData = (columnIndex, date, jobData) => {
        // const formattedDate = date.format('YYYY-MM-DD');
        // setJobsByDate(prevJobs => {
        //     const updatedJobs = {
        //         ...prevJobs,
        //         [formattedDate]: jobData,
        //     };
        //     // console.log("Updated jobsByDate:", updatedJobs);
        //     return updatedJobs;
        // });
    }
    /**
     * Update a job's crew assignment.
     * @param {string} date - The date of the job (formatted 'YYYY-MM-DD').
     * @param {number} jobIndex - The index of the job within the date.
     * @param {number} crewIndex - The index of the crew assigned to the job.
     */
    // const assignCrewToJob = useCallback((date, jobIndex, crewIndex) => {
    //     const job = jobsByDate[date][jobIndex];
    //     const crew = '[' + crews[crewIndex].employees.map(emp => emp.oid) + ']';

    //     CentralCalls.upsertData({ oid: job.phase_status_oid, employee_oids: crew }, 'phase_status', 'update');
    //     CentralCalls.upsertData({ oid: job.phase_oid, date_scheduled: CentralCalls.sqlFormatdate(date) }, 'phase', 'update');
    //     setJobsByDate(prevJobs => {
    //         const updatedJobs = [...prevJobs[date]];
    //         updatedJobs[jobIndex].employee_oids = crews[crewIndex].employees.map(emp => emp.oid);
    //         updatedJobs[jobIndex].crewIndex = crewIndex;
    //         //console.log('from assignCrewToJob new jobsByDate:', updatedJobs);
    //         return {
    //             ...prevJobs,
    //             [date]: updatedJobs,
    //         };
    //     });
    // }, [crews]);
    // const selectedEmployees = useMemo(
    //     () => crews[selectedCrewIndex]?.employees ?? [],
    //     [crews, selectedCrewIndex]
    // );

    /**
     * Handle date change from ScheduleColumn.
     * @param {number} columnIndex - The index of the column whose date has changed.
     * @param {dayjs} newDate - The new date selected.
     */
    const handleCrewReOrderDrop = (item, aboveIndex, belowIndex, crewListType) => {
       
        // Clone both lists
        const updatedCrews = [...crews];
        const updatedAddedCrews = [...addedCrews];

        const isPrimary = crewListType === 'primary';
        const sourceList = isPrimary ? updatedCrews : updatedAddedCrews;
        const targetList = isPrimary ? updatedCrews : updatedAddedCrews;

        const itemIndex = sourceList.findIndex(crew => crew.team_oid === item.crew.team_oid);
        if (itemIndex === -1) return;

        const [movedItem] = sourceList.splice(itemIndex, 1);

        const insertIndex = belowIndex === -1 ? targetList.length : belowIndex;
        const belowItem = targetList[insertIndex]; // capture before inserting

        targetList.splice(insertIndex, 0, movedItem);

        // ✅ Update order_num so React renders correctly
        targetList.forEach((crew, i) => {
            crew.order_num = i;
        });

        // ✅ Set state so React re-renders correctly
        setCrews([...updatedCrews]);
        setAddedCrews([...updatedAddedCrews]);

        const saveOrder = async () => {
            try {
                const above_oid = movedItem.team_oid;
                const below_oid = belowItem ? belowItem.team_oid : null;

                await CentralCalls.callStoredProcedureWithParams(
                    'sp_SwapCrewOrderNums', // or sp_shiftCrewOrder
                    {
                        above_employee_team_oid: above_oid,
                        below_employee_team_oid: below_oid,
                        is_primary: isPrimary ? 1 : 0
                    }
                );
            } catch (error) {
                console.error('Error updating crew order:', error);
            }
        };

        saveOrder();
    };




    const handleSelectedDateChange = (newDate) => {
        if (!newDate) return; // Prevent errors if no date is selected

        setStartDate(newDate);
        setColumns(prevColumns => {
            const newColumns = [...prevColumns]; // Copy existing state
            let nextDate = newDate; // Start updating from index 1
            for (let i = 1; i < prevColumns.length; i++) {
                newColumns[i] = nextDate;
                nextDate = nextDate.add(1, 'day'); // Increment each new date
            }
            return newColumns;
        });
    };
    const handleToggleCollapse = (index) => {
        setCollapsed(prevState => {
            const newState = [...prevState];
            newState[index] = !newState[index];
            return newState;
        });
    };
const AddMissingCrews = () => {
    const allJobs = columnRefs.current.flatMap(ref => ref?.getJobs() || []);
    //console.log('alljobs...', allJobs);
    const usedCrewIds = new Set(allJobs.map(job => job.team_oid).filter(Boolean));
   // console.log('usedCrewIds:', usedCrewIds);

    // Only consider addedCrews for exclusion
    const existingCrewIds = new Set(addedCrews.map(c => c.team_oid));

    // Find any missing crew IDs that are in jobs but not in addedCrews
    const missingCrewIds = [...usedCrewIds].filter(id => !existingCrewIds.has(id));

    if (missingCrewIds.length === 0) return;

    // Pull full crew objects from crews for these IDs
    const crewsToAdd = allCrews.filter( c => missingCrewIds.includes(c.team_oid) && c.is_primary !== 1);

    // Append them to addedCrews
    setAddedCrews(prev => [...prev, ...crewsToAdd]);
};




    const MemoizedScheduleColumn = React.memo(ScheduleColumn);

    return (

        <div className="match-schedule-root">
            <div className="match-schedule-column crew">
                <div className="match-schedule-header">
                    <div className="filters-container">
                        <div className="filter-group">
                            <span className="filter-title">Builder</span>
                            <BuilderFilter onBuilderSelect={handleBuilderSelect} />
                        </div>
                        {/* <div className="filter-group">
                            <span className="filter-title">Crew</span>
                            <CrewFilter onCrewSelect={handleCrewFilterSelect} />
                        </div> */}
                        <div className="filter-group">
                            <span className="filter-title">Phase</span>
                            <PhaseFilter onPhaseSelect={handlePhaseTypeSelect} />
                        </div>
                        <div className="filter-group">
                            <span className="filter-title">Status</span>
                            <ReadyStatFilter onStatusSelect={handlePhaseStatusSelect} />
                        </div>
                        <div className="filter-group">
                            <span className="filter-title">Add Days</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <IconButton onClick={addCustomDate} size="small">
                                    <AddIcon />
                                </IconButton>
                                <IconButton onClick={removeCustomDate} size="small" disabled={columns.length === 2}>
                                    <RemoveIcon />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Schedule Columns */}
                <div className="match-schedule-columns">
                    {/* <DndProvider backend={HTML5Backend}> */}
                    <div className="crew-list-wrapper">
                        {/* Fixed Icon Header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <TeamDropDown key={teamDropDownKey} onAddCrew={addExistingCrew} currentCrews={addedCrews}>    </TeamDropDown>
                            <span className='title' style={{ marginLeft: '15px' }}>
                                Crew
                                <IconButton onClick={addNewCrew} size="small">
                                    <AddIcon />
                                </IconButton>
                            </span>
                            <IconButton onClick={AddMissingCrews} size="small">
                                <ArrowDownwardRounded />
                            </IconButton>
                        </div>
                        {/* Scrollable Crew List */}
                        <div className="crew-list-column">
                            {crews?.sort((a, b) => a.order_num - b.order_num).map((crew, index) => (
                                <React.Fragment key={index}>
                                    {/* {index > 0 && ( */}
                                    <CrewDropZone
                                        key={`drop-${index}`}
                                        aboveIndex={index - 1}
                                        belowIndex={index}
                                        crewListType={'primary'}
                                        onDrop={(item, aboveIndex, belowIndex, crewListType) => {
                                            handleCrewReOrderDrop(item, aboveIndex, belowIndex, crewListType);
                                        }}
                                    />
                                    {/* )} */}
                                    <DraggableCrewList
                                        crew={crew}
                                        // onClick={() => setSelectedCrewIndex(index)}
                                        onDelete={() => deleteCrew(index)}
                                        showDelete={false}
                                        isSelected={crew.team_oid === selectedCrewOid}
                                        index={index}
                                        crewColors={crewColors}
                                        onRefresh={refreshCrews}
                                        onFilterClick={(crew) => {
                                            if (filterCrew && filterCrew.length === 1 && filterCrew[0] === crew.team_oid) {
                                                
                                                setSelectedCrewOid(null);
                                                setFilterCrew(null);
                                            }
                                            else {
                                            
                                                setFilterCrew([crew.team_oid]);
                                                setSelectedCrewOid(crew.team_oid);
                                            }
                                        }}
                                    />
                                </React.Fragment>
                            ))}
                            <div style={{ borderTop: '1px solid #ccc', margin: '16px 0' }} />
                            {addedCrews?.sort((a, b) => a.order_num - b.order_num).map((crew, index) => (
                                <React.Fragment key={index}>

                                    <CrewDropZone
                                        key={`drop-${index}`}
                                        aboveIndex={index - 1}
                                        belowIndex={index}
                                        crewListType={'nonprimary'}
                                        onDrop={(item, aboveIndex, belowIndex, crewListType) => {
                                            handleCrewReOrderDrop(item, aboveIndex, belowIndex, crewListType);
                                        }}
                                    />

                                    <DraggableCrewList
                                        key={index}
                                        crew={crew}
                                        // onClick={() =>{console.log('onclidk'); setSelectedCrewIndex(index)}}
                                        onDelete={() => { deleteCrew(index) }}
                                        showDelete={true}
                                        isSelected={crew.team_oid === selectedCrewOid}
                                        index={index}
                                        crewColors={crewColors}
                                        onRefresh={refreshCrews}
                                        onFilterClick={(crew) => {
                                            if (filterCrew && filterCrew.length === 1 && filterCrew[0] === crew.team_oid) {
                                               
                                                setSelectedCrewOid(null);
                                                setFilterCrew(null);
                                            }
                                            else {
                                               
                                                setFilterCrew([crew.team_oid]);
                                                setSelectedCrewOid(crew.team_oid);
                                            }
                                        }}
                                    />
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', width: '100%', gap: '8px', flexGrow: 1, margin: 0, padding: 0 }}>
                        {columns.map((date, index) => {
                            const isCollapsed = collapsed[index];
                            const totalNonCollapsed = collapsed.filter(c => !c).length || 1; // Ensure at least 1 open column

                            return (
                                <div
                                    key={date.format('YYYY-MM-DD')}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: 'calc(100vh - 120px)',
                                        overflowY: 'auto',
                                        flex: isCollapsed ? '0 0 30px' : `1 1 ${100 / totalNonCollapsed}%`, // Collapsed takes 30px, others share space
                                        minWidth: isCollapsed ? '30px' : '0' // Ensures collapsed columns stay small
                                    }}
                                >
                                    {/* Header Section */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {!isCollapsed && index !== 0 && (

                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    {index === 1 && (
                                                        <>
                                                            <IconButton onClick={() => setCalendarOpen(true)}>
                                                                <CalendarIcon />
                                                            </IconButton>

                                                            <DatePicker
                                                                open={calendarOpen}
                                                                value={startDate}
                                                                onChange={(newDate) => handleSelectedDateChange(newDate)} // Update state when date is selected
                                                                onClose={() => setCalendarOpen(false)}
                                                                sx={{ display: "none" }} // Hide default input field
                                                            />
                                                        </>
                                                    )}

                                                    <span className="title">{date.format("ddd")} {date.format("MM-DD")}</span>
                                                     {index !== 0 && selectedCrewOid && (
                                                       <IconButton onClick={() => handlePrintTicketsClick(date)} size="small">
                                                             <Print />
                                                         </IconButton>
                                                    )}
                                                    {/* <RefreshIcon 
                                                        style={{ 
                                                          cursor: 'pointer', 
                                                          color: '#666',
                                                          marginLeft: '8px'
                                                        }} 
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          refreshColumnByDate(date.format('YYYY-MM-DD'));
                                                        }} 
                                                        titleAccess="Refresh this column"
                                                      />
                                                     */}
                                                </div>
                                            </LocalizationProvider>

                                        )}
                                        {!isCollapsed && index === 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <RefreshIcon
                                                    style={{
                                                        cursor: 'pointer',
                                                        color: '#666',
                                                        marginLeft: '8px'
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        refreshColumnByDate(date.format('YYYY-MM-DD'));
                                                    }}
                                                    titleAccess="Refresh this column"
                                                />
                                                <span className='title' style={{ marginLeft: '15px', textDecoration: 'underline' }}>
                                                    Past Due
                                                </span>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleFixMissingPhaseStatusAll}
                                                    title="Fix missing phase status records"
                                                    aria-label="Fix missing phase status records"
                                                    style={{ marginLeft: '6px' }}
                                                >
                                                    <AutoFixHighIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        )}
                                        <IconButton onClick={() => handleToggleCollapse(index)} size="small">
                                            {isCollapsed ? (index === 0 ? <WarningIcon /> : <CalendarIcon />) : <ExpandLess />}
                                        </IconButton>

                                    </div>

                                    {/* Summary Only When Not Collapsed */}


                                    {/* Schedule Column */}
                                    <MemoizedScheduleColumn
                                        ref={el => columnRefs.current[index] = el}
                                        columnIndex={index}
                                        selectedDate={date}
                                        isPastDue={index === 0}
                                        refreshColumnByDate={refreshColumnByDate}
                                        refreshTrigger={refreshKeyByDate[date.format('YYYY-MM-DD')]}
                                        crewColors={crewColors}
                                        isCollapsed={isCollapsed}
                                        filterPhaseStatus={filterPhaseStatus}
                                        filterPhaseType={filterPhaseType}
                                        filterBuilder={filterBuilder}
                                        filterCrew={filterCrew}
                                        key={date}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    {/* </DndProvider> */}
                </div>

            </div>
            <Dialog fullWidth={true} maxWidth='lg' open={crewDialogOpen} onClose={() => setCrewDialogOpen(false)}>
                <DialogContent >
                    <>
                        <h2>New Crew</h2>
                        <CrewSelector
                            selectedEmployees={crews && crews[selectedCrewOid] ? crews[selectedCrewOid].employees : []}
                            onSave={() => { setRefreshCrewList(!refreshCrewList); setCrewDialogOpen(false); }}
                        />
                    </>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCrewDialogOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
export default MatchSchedule;